/* eslint-disable react/prop-types */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import {
  BadgeCheck, CircleCheck, CircleX, Clock, ExternalLink, Hourglass, ImagePlus, Instagram, Loader2,
  Music2, Plane, ShieldCheck, Sparkles, Upload, Video, X, Youtube,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import SectionBand from '@/components/ui/SectionBand';
import RoyalAmbientBackground from '@/components/ui/RoyalAmbientBackground';
import ActionModalShell from '@/components/ui/ActionModalShell';
import { cn } from '@/lib/utils';
import { earnCreditApi } from '@/lib/earnCreditApi';
import { compressScreenshot } from '@/utils/screenshotCompression';
import {
  BRAND_NESTED_ROW, BRAND_PRIMARY_BTN_CLASS, DASHBOARD_FILTER_INPUT, REWARDS_SECTION_CARD,
} from '@/constants/brandColors';
import {
  EARN_CREDIT_ACTIONS,
  EARN_CREDIT_MAX_SCREENSHOTS,
  EARN_CREDIT_POST_REQUIREMENTS,
  EARN_CREDIT_TERMS,
  earnCreditAction,
  formatEarnAmount,
} from '@/constants/earnCreditProgram';

/**
 * /earn: Earn Defera Credit. A customer with a booked trip posts a video about
 * Defera, submits it here, and the team reviews it into Defera Credit.
 * Amounts and rules come from src/constants/earnCreditProgram.js; the server
 * (server/earn-credit.js) decides eligibility and what can be earned now.
 */

const PLATFORM_ICONS = { TikTok: Music2, Instagram, YouTube: Youtube };

function PlatformGlyph({ platform, className }) {
  const Icon = PLATFORM_ICONS[platform] || Video;
  return <Icon className={className} aria-hidden="true" />;
}

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '');

function StatusPill({ submission }) {
  const { status, credit_added: creditAdded, amount_cents: cents } = submission;
  const map = {
    pending: { icon: Hourglass, label: 'In review', cls: 'border-amber-400/25 bg-amber-400/10 text-amber-200' },
    approved: creditAdded
      ? { icon: CircleCheck, label: `${formatEarnAmount(cents)} added`, cls: 'border-emerald-400/25 bg-emerald-400/10 text-emerald-200' }
      : { icon: CircleCheck, label: 'Approved, credit on its way', cls: 'border-emerald-400/25 bg-emerald-400/10 text-emerald-200' },
    rejected: { icon: CircleX, label: 'Not approved', cls: 'border-rose-400/25 bg-rose-400/10 text-rose-200' },
    withdrawn: { icon: X, label: 'Withdrawn', cls: 'border-white/15 bg-white/5 text-white/60' },
  };
  const s = map[status] || map.pending;
  const Icon = s.icon;
  return (
    <span className={cn('inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-normal whitespace-nowrap', s.cls)}>
      <Icon className="h-3 w-3" aria-hidden="true" />
      {s.label}
    </span>
  );
}

function SubmitPostModal({ action, reviewWindow, onClose, onSubmitted }) {
  const [postUrl, setPostUrl] = useState('');
  const [handle, setHandle] = useState('');
  const [note, setNote] = useState('');
  const [shots, setShots] = useState([]);
  const [accepted, setAccepted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [preparing, setPreparing] = useState(false);
  const [error, setError] = useState('');
  const [showTerms, setShowTerms] = useState(false);

  const addFiles = async (fileList) => {
    const files = Array.from(fileList || []).slice(0, EARN_CREDIT_MAX_SCREENSHOTS - shots.length);
    if (!files.length) return;
    setPreparing(true);
    setError('');
    try {
      const next = [];
      for (const f of files) next.push(await compressScreenshot(f));
      setShots((prev) => [...prev, ...next].slice(0, EARN_CREDIT_MAX_SCREENSHOTS));
    } catch (e) {
      setError(e.message);
    } finally {
      setPreparing(false);
    }
  };

  const canSubmit = accepted && handle.trim() && (postUrl.trim() || shots.length) && !busy && !preparing;

  const submit = async () => {
    setBusy(true);
    setError('');
    try {
      const { submission } = await earnCreditApi.submit({
        action_key: action.key,
        post_url: postUrl.trim() || null,
        handle: handle.trim(),
        note: note.trim() || null,
        screenshots: shots,
        accept_terms: true,
      });
      onSubmitted(submission);
    } catch (e) {
      setError(e.message);
      setBusy(false);
    }
  };

  return (
    <ActionModalShell
      isOpen
      onClose={busy ? () => {} : onClose}
      icon={PLATFORM_ICONS[action.platform] || Video}
      title={`Submit your ${action.name}`}
      description={`${formatEarnAmount(action.amountCents)} in Defera Credit once it's approved. We review within ${reviewWindow}.`}
      maxWidthClass="sm:max-w-lg"
      footer={(
        <Button type="button" onClick={submit} disabled={!canSubmit} className={cn('w-full h-11 rounded-xl', BRAND_PRIMARY_BTN_CLASS)}>
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Submit for review'}
        </Button>
      )}
    >
      <div className="space-y-5">
        <div className={cn(BRAND_NESTED_ROW, 'px-4 py-3 text-[13px] text-white/80 leading-relaxed')}>
          <span className="text-white/90 font-medium">Disclosure first.</span>{' '}
          {action.disclosureStep} Then add <span className="text-white/90">#ad</span> to your caption.
        </div>

        <label className="block space-y-1.5">
          <span className="text-[13px] text-white/85">Link to your post</span>
          <Input
            value={postUrl}
            onChange={(e) => setPostUrl(e.target.value)}
            placeholder={action.urlHint}
            inputMode="url"
            autoCapitalize="none"
            autoCorrect="off"
            className={cn(DASHBOARD_FILTER_INPUT, 'h-11 rounded-xl')}
          />
          <span className="block text-[11px] text-white/45">Copy it from the post’s Share button. No link? Add screenshots instead.</span>
        </label>

        <label className="block space-y-1.5">
          <span className="text-[13px] text-white/85">Your {action.platform} handle</span>
          <Input
            value={handle}
            onChange={(e) => setHandle(e.target.value)}
            placeholder="@yourname"
            autoCapitalize="none"
            autoCorrect="off"
            className={cn(DASHBOARD_FILTER_INPUT, 'h-11 rounded-xl')}
          />
        </label>

        <div className="space-y-2">
          <span className="block text-[13px] text-white/85">Screenshots <span className="text-white/45">(optional, up to {EARN_CREDIT_MAX_SCREENSHOTS})</span></span>
          <div className="flex flex-wrap gap-2.5">
            {shots.map((src, i) => (
              <div key={src.slice(-24)} className="relative h-20 w-14 overflow-hidden rounded-lg border border-white/15">
                <img src={src} alt={`Screenshot ${i + 1}`} className="h-full w-full object-cover" />
                <button
                  type="button"
                  onClick={() => setShots((prev) => prev.filter((_, j) => j !== i))}
                  className="absolute right-0.5 top-0.5 rounded-full bg-black/70 p-0.5 text-white/85 hover:text-white"
                  aria-label={`Remove screenshot ${i + 1}`}
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            ))}
            {shots.length < EARN_CREDIT_MAX_SCREENSHOTS && (
              <label className="flex h-20 w-14 cursor-pointer flex-col items-center justify-center gap-1 rounded-lg border border-dashed border-white/20 text-white/50 hover:border-white/35 hover:text-white/75">
                {preparing ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />}
                <span className="text-[10px]">Add</span>
                <input type="file" accept="image/*" multiple className="sr-only" onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }} />
              </label>
            )}
          </div>
        </div>

        <label className="block space-y-1.5">
          <span className="text-[13px] text-white/85">Anything we should know? <span className="text-white/45">(optional)</span></span>
          <Textarea
            value={note}
            onChange={(e) => setNote(e.target.value.slice(0, 500))}
            rows={2}
            className={cn(DASHBOARD_FILTER_INPUT, 'rounded-xl text-sm')}
          />
        </label>

        <div className="space-y-2">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={accepted}
              onChange={(e) => setAccepted(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-white/30 bg-transparent accent-brand-500"
            />
            <span className="text-[13px] text-white/80 leading-relaxed">
              This is my post, it’s disclosed as above, and I accept the{' '}
              <button type="button" onClick={() => setShowTerms((v) => !v)} className="underline underline-offset-2 text-white/90 hover:text-white">program terms</button>
              , including Defera’s right to share and reuse it.
            </span>
          </label>
          {showTerms && (
            <ul className="ml-7 list-disc space-y-1.5 pl-4 text-[12px] text-white/60 leading-relaxed">
              {EARN_CREDIT_TERMS.map((t) => <li key={t}>{t}</li>)}
            </ul>
          )}
        </div>

        {error && <p role="alert" className="rounded-xl border border-rose-400/25 bg-rose-400/10 px-3.5 py-2.5 text-[13px] text-rose-100">{error}</p>}
      </div>
    </ActionModalShell>
  );
}

function WaysToEarn({ data, onPick }) {
  return (
    <div className="grid gap-4 sm:grid-cols-3">
      {data.actions.map((a) => {
        const action = earnCreditAction(a.key);
        const disabled = !data.eligible || !a.available;
        // Without a trip the banner above explains; the cards don't repeat it.
        const hint = !data.eligible ? null
          : a.reason === 'pending' ? 'Your last one is in review'
            : a.reason === 'recent' ? `Next post from ${fmtDate(a.next_available_at)}` : null;
        return (
          <div key={a.key} className={cn(REWARDS_SECTION_CARD, 'flex flex-col p-5')}>
            <div className="flex items-center justify-between">
              <span className="flex h-10 w-10 items-center justify-center rounded-2xl border border-brand-800/40 bg-brand-900/40">
                <PlatformGlyph platform={a.platform} className="h-5 w-5 text-brand-200" />
              </span>
              <span className="text-[11px] uppercase tracking-widest text-white/45">{a.platform}</span>
            </div>
            <div className="mt-4 text-3xl font-light tracking-tight text-white">{formatEarnAmount(a.amount_cents)}</div>
            <div className="mt-0.5 text-[13px] text-white/60">Defera Credit per approved {a.name}</div>
            <p className="mt-3 flex-1 text-[12px] text-white/50 leading-relaxed">{action?.disclosureStep}</p>
            <Button
              type="button"
              disabled={disabled}
              onClick={() => onPick(action)}
              className={cn('mt-4 h-10 w-full rounded-xl text-sm', BRAND_PRIMARY_BTN_CLASS)}
            >
              <Upload className="mr-2 h-4 w-4" /> Submit a post
            </Button>
            {hint && <p className="mt-2 text-center text-[11px] text-white/45">{hint}</p>}
          </div>
        );
      })}
    </div>
  );
}

function Submissions({ submissions, onWithdraw, withdrawing }) {
  if (!submissions.length) return null;
  return (
    <div className={REWARDS_SECTION_CARD}>
      <SectionBand icon={BadgeCheck} title="Your submissions" subtitle="What you’ve sent and where each one stands" />
      <ul className="divide-y divide-brand-800/35">
        {submissions.map((s) => (
          <li key={s.id} className="flex flex-col gap-2 px-5 py-4 sm:flex-row sm:items-start sm:justify-between">
            <div className="flex min-w-0 items-start gap-3">
              <PlatformGlyph platform={s.platform} className="mt-0.5 h-4 w-4 flex-shrink-0 text-white/50" />
              <div className="min-w-0">
                <p className="text-[14px] text-white/90">{s.name} · {formatEarnAmount(s.amount_cents)}</p>
                <p className="text-[12px] text-white/45">
                  Sent {fmtDate(s.created_at)}{s.handle ? ` · @${s.handle}` : ''}
                  {s.post_url && (
                    <>
                      {' · '}
                      <a href={s.post_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-white/60 hover:text-white/85">
                        View post <ExternalLink className="h-3 w-3" />
                      </a>
                    </>
                  )}
                </p>
                {s.status === 'rejected' && s.review_reason && (
                  <p className="mt-1.5 text-[13px] text-rose-100/90 leading-relaxed">{s.review_reason}</p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-3 pl-7 sm:pl-0">
              <StatusPill submission={s} />
              {s.status === 'pending' && (
                <button
                  type="button"
                  onClick={() => onWithdraw(s)}
                  disabled={withdrawing === s.id}
                  className="text-[12px] text-white/45 underline-offset-2 hover:text-white/75 hover:underline disabled:opacity-50"
                >
                  {withdrawing === s.id ? 'Withdrawing…' : 'Withdraw'}
                </button>
              )}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function EarnCredit() {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [picked, setPicked] = useState(null);
  const [justSent, setJustSent] = useState(null);
  const [withdrawing, setWithdrawing] = useState(null);

  const load = useCallback(async () => {
    setError('');
    try {
      setData(await earnCreditApi.overview());
    } catch (e) {
      setError(e.message);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // "$100" when every platform pays the same, "up to $X" once they differ.
  const amountLine = useMemo(() => {
    const amounts = EARN_CREDIT_ACTIONS.map((a) => a.amountCents);
    const top = formatEarnAmount(Math.max(...amounts));
    return new Set(amounts).size === 1 ? top : `up to ${top}`;
  }, []);

  const withdraw = async (s) => {
    setWithdrawing(s.id);
    try {
      await earnCreditApi.withdraw(s.id);
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setWithdrawing(null);
    }
  };

  let body;
  if (error && !data) {
    body = (
      <div className={cn(REWARDS_SECTION_CARD, 'p-8 text-center')}>
        <p className="text-sm text-white/80">{error}</p>
        <Button type="button" variant="outline" onClick={load} className="mt-4 rounded-xl border-white/15 bg-white/5 text-white hover:bg-white/10">Try again</Button>
      </div>
    );
  } else if (!data) {
    body = <div className="flex justify-center py-16"><Loader2 className="h-5 w-5 animate-spin text-white/40" /></div>;
  } else if (!data.enabled) {
    body = (
      <div className={cn(REWARDS_SECTION_CARD, 'p-8 text-center')}>
        <p className="text-sm text-white/80">Earn Defera Credit isn’t open yet.</p>
        <Link to="/rewards" className="mt-3 inline-block text-[13px] text-white/60 underline underline-offset-2 hover:text-white">Back to Rewards</Link>
      </div>
    );
  } else {
    body = (
      <div className="space-y-8">
        {!data.eligible && (
          <div className={cn(REWARDS_SECTION_CARD, 'flex flex-col items-start gap-4 p-6 sm:flex-row sm:items-center sm:justify-between')}>
            <div className="flex items-start gap-3">
              <Plane className="mt-0.5 h-5 w-5 text-brand-300" />
              <div>
                <p className="text-[15px] text-white/90">Book a trip to start earning</p>
                <p className="text-[13px] text-white/55">The program is for customers who’ve booked with Defera, so your post can speak from experience.</p>
              </div>
            </div>
            <Button asChild className={cn('h-10 rounded-xl px-5 text-sm', BRAND_PRIMARY_BTN_CLASS)}>
              <Link to="/search">Find a flight</Link>
            </Button>
          </div>
        )}

        {justSent && (
          <div role="status" className="flex items-start gap-3 rounded-2xl border border-emerald-400/25 bg-emerald-400/10 px-5 py-4">
            <CircleCheck className="mt-0.5 h-5 w-5 text-emerald-300" />
            <div>
              <p className="text-[14px] text-emerald-50">Got it. Your {justSent.name} is in review.</p>
              <p className="text-[13px] text-emerald-100/75">We’ll review it within {data.review_window}. Approved credit goes straight to your Defera Credit balance, and we’ll email you either way.</p>
            </div>
          </div>
        )}

        <WaysToEarn data={data} onPick={setPicked} />

        <div className="grid gap-4 md:grid-cols-2">
          <div className={REWARDS_SECTION_CARD}>
            <SectionBand icon={ShieldCheck} title="What your post needs" />
            <ul className="space-y-3 p-5">
              {EARN_CREDIT_POST_REQUIREMENTS.map((r) => (
                <li key={r} className="flex gap-3 text-[13px] text-white/80 leading-relaxed">
                  <CircleCheck className="mt-0.5 h-4 w-4 flex-shrink-0 text-brand-300" aria-hidden="true" />
                  {r}
                </li>
              ))}
            </ul>
          </div>
          <div className={REWARDS_SECTION_CARD}>
            <SectionBand icon={Clock} title="How it works" />
            <ol className="space-y-4 p-5">
              {[
                ['Post', 'Film yourself talking about your Defera trip and post it from your public account, with the platform’s paid-partnership label and #ad.'],
                ['Submit', 'Send us the link (or screenshots) and the handle you posted from.'],
                ['Earn', `We review within ${data.review_window}. Approved credit lands in your Defera Credit balance and goes toward your trips.`],
              ].map(([title, text], i) => (
                <li key={title} className="flex gap-3">
                  <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full border border-brand-700/50 bg-brand-900/50 text-[12px] text-white/85">{i + 1}</span>
                  <div>
                    <p className="text-[14px] text-white/90">{title}</p>
                    <p className="text-[13px] text-white/60 leading-relaxed">{text}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </div>

        <Submissions submissions={data.submissions} onWithdraw={withdraw} withdrawing={withdrawing} />

        <details className={cn(REWARDS_SECTION_CARD, 'group')}>
          <summary className="cursor-pointer list-none px-5 py-4 text-[14px] text-white/85 hover:text-white">
            Program terms <span className="text-white/40 group-open:hidden">· show</span>
          </summary>
          <ul className="list-disc space-y-2 px-10 pb-5 text-[13px] text-white/60 leading-relaxed">
            {EARN_CREDIT_TERMS.map((t) => <li key={t}>{t}</li>)}
          </ul>
        </details>

        {error && <p role="alert" className="text-center text-[13px] text-rose-200">{error}</p>}
      </div>
    );
  }

  return (
    <div className="relative min-h-screen overflow-x-hidden bg-brand-950 text-white">
      <Helmet><title>Earn Defera Credit · Defera</title></Helmet>
      <RoyalAmbientBackground />
      <div className="relative z-10 mx-auto max-w-5xl px-4 pb-20 pt-16 sm:px-6 lg:px-8">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="mb-10 text-center">
          <span className="inline-flex items-center gap-1.5 rounded-full border border-brand-700/40 bg-brand-900/40 px-3 py-1 text-[11px] uppercase tracking-widest text-white/60">
            <Sparkles className="h-3 w-3 text-brand-300" /> Defera Credit
          </span>
          <h1 className="mt-5 text-4xl font-thin tracking-tight sm:text-5xl">Earn Defera Credit</h1>
          <p className="mx-auto mt-4 max-w-xl text-lg font-light text-white/65">
            Post a video about your Defera trip on TikTok, Instagram or YouTube and get {amountLine} in Defera Credit for every post we approve.
          </p>
        </motion.div>
        {body}
      </div>

      {picked && data?.enabled && (
        <SubmitPostModal
          action={picked}
          reviewWindow={data.review_window}
          onClose={() => setPicked(null)}
          onSubmitted={(s) => { setPicked(null); setJustSent(s); load(); }}
        />
      )}
    </div>
  );
}
